import React, { useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { Label } from "../ui/Label";
import { SuccessMessage } from "../ui/SuccessMessage";
import { ErrorMessage } from "../ui/ErrorMessage";
import { LogoUpload } from "./LogoUpload";
import { Organisation } from "../../domain/types/organisation.types";
import { OrganisationService } from "../../services/organisation.service";
import { AuthService } from "../../services/auth.service";

const practiceSchema = z.object({
  name: z.string().min(2, "Practice name must be at least 2 characters"),
  address: z.string().optional(),
  phoneNumber: z
    .string()
    .optional()
    .refine(
      (value) => {
        // If empty or undefined, it's valid (optional field)
        if (!value || value.trim() === "") return true;
        // If provided, must be a valid phone number
        return isValidPhoneNumber(value);
      },
      { message: "Please enter a valid phone number" }
    ),
});

type PracticeSchemaType = z.infer<typeof practiceSchema>;

interface PracticeInfoProps {
  organisation: Organisation;
  onUpdate: (organisation: Organisation) => void;
}

export const PracticeInfo: React.FC<PracticeInfoProps> = ({ organisation, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Check if user has ROLE_ORG_ADMIN
  const currentUser = AuthService.getCurrentUser();
  const isOrgAdmin = currentUser?.roles?.includes("ROLE_ORG_ADMIN") ?? false;

  // Store original values to track changes
  const originalValues = useMemo(
    () => ({
      name: organisation.name,
      address: organisation.address || "",
      phoneNumber: organisation.phoneNumber || "",
    }),
    [organisation.name, organisation.address, organisation.phoneNumber]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm<PracticeSchemaType>({
    resolver: zodResolver(practiceSchema),
    defaultValues: originalValues,
  });

  // Watch all form fields to detect changes
  const formValues = watch();

  // Determine if there are actual changes compared to original values
  const hasChanges = useMemo(() => {
    const currentName = formValues.name || "";
    const currentAddress = formValues.address || "";
    const currentPhoneNumber = formValues.phoneNumber || "";

    return (
      currentName !== originalValues.name ||
      currentAddress !== originalValues.address ||
      currentPhoneNumber !== originalValues.phoneNumber
    );
  }, [formValues, originalValues]);

  const onSubmit = async (data: PracticeSchemaType) => {
    setIsLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      // Build payload with only changed fields
      const changedFields: {
        name?: string;
        address?: string;
        phoneNumber?: string;
      } = {};

      const currentName = data.name || "";
      const currentAddress = data.address || "";
      const currentPhoneNumber = data.phoneNumber || "";

      // Add field to payload if it changed from original value
      if (currentName !== originalValues.name) {
        changedFields.name = data.name;
      }
      if (currentAddress !== originalValues.address) {
        // If field was filled but now empty, send empty string
        // If field value changed, send new value
        changedFields.address = currentAddress;
      }
      if (currentPhoneNumber !== originalValues.phoneNumber) {
        // If field was filled but now empty, send empty string
        // If field value changed, send new value
        changedFields.phoneNumber = currentPhoneNumber;
      }

      const updatedOrganisation = await OrganisationService.updateOrganisation(
        organisation.id,
        changedFields
      );

      onUpdate(updatedOrganisation);
      setIsEditing(false);
      setSuccessMessage("Practice information updated successfully!");
    } catch (error) {
      console.error("Failed to update practice information:", error);
      setErrorMessage("Failed to update practice information. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    reset(originalValues);
    setIsEditing(false);
    setErrorMessage("");
  };

  const handleLogoUpload = async (file: File) => {
    setIsUploadingLogo(true);
    setErrorMessage("");

    try {
      const response = await OrganisationService.uploadLogo(organisation.id, file);

      // Fetch updated organisation data to get the new logo URL
      const updatedOrganisation = await OrganisationService.getOrganisation(organisation.name);
      onUpdate(updatedOrganisation);

      setSuccessMessage("Logo uploaded successfully!");
    } catch (error) {
      console.error("Failed to upload logo:", error);
      setErrorMessage("Failed to upload logo. Please try again.");
    } finally {
      setIsUploadingLogo(false);
    }
  };

  return (
    <div className="bg-white border border-border-gray rounded-none p-6 md:w-4/6">
      <div className="mb-6">
        <h3 className="text-base font-bold uppercase tracking-wide">PRACTICE INFORMATION</h3>
        <p className="text-sm text-gray-600 mt-2">Update your Practice Details</p>
      </div>

      {successMessage && (
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage("")}
          className="mb-6"
        />
      )}

      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onDismiss={() => setErrorMessage("")}
          className="mb-6"
        />
      )}

      {isOrgAdmin && (
        <div className="mb-6">
          <Label className="block text-sm font-medium text-black mb-3">Practice Logo</Label>
          <LogoUpload
            currentLogoUrl={organisation.logoUrl}
            onUpload={handleLogoUpload}
            isUploading={isUploadingLogo}
          />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-0 max-w-2xl">
        {/* Practice Name and Practice ID Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="relative pb-5">
            <Label htmlFor="name" className="block text-sm font-medium text-black mb-2">
              Practice Name
            </Label>
            <Input
              id="name"
              {...register("name")}
              disabled={!isEditing || isLoading}
              error={!!errors.name}
              className="w-full h-12 bg-disabled-bg border-border-gray rounded-none disabled:bg-disabled-bg disabled:cursor-not-allowed placeholder:text-gray-500"
            />
            {errors.name && (
              <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                {errors.name.message}
              </p>
            )}
          </div>
        </div>

        {/* Address and Phone Number Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="relative pb-5">
            <Label htmlFor="address" className="block text-sm font-medium text-black mb-2">
              Address
            </Label>
            <Input
              id="address"
              {...register("address")}
              disabled={!isEditing || isLoading}
              error={!!errors.address}
              className="w-full h-12 bg-disabled-bg border-border-gray rounded-none disabled:bg-disabled-bg disabled:cursor-not-allowed placeholder:text-gray-500"
              placeholder="Enter Practice Address"
            />
            {errors.address && (
              <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                {errors.address.message}
              </p>
            )}
          </div>

          <div className="relative pb-5">
            <Label htmlFor="phoneNumber" className="block text-sm font-medium text-black mb-2">
              Phone Number <span className="text-gray-400 text-xs">(Optional)</span>
            </Label>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  countries={["US"]}
                  defaultCountry="US"
                  value={field.value || ""}
                  onChange={field.onChange}
                  disabled={!isEditing || isLoading}
                  className="flex"
                  numberInputProps={{
                    className: `w-full h-12 bg-disabled-bg border border-border-gray rounded-none px-3 py-2 text-base outline-none focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/20 disabled:bg-disabled-bg disabled:cursor-not-allowed placeholder:text-gray-500 ${
                      errors.phoneNumber ? "border-red-500" : ""
                    }`,
                  }}
                />
              )}
            />
            {errors.phoneNumber && (
              <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
                {errors.phoneNumber.message}
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {isOrgAdmin && (
          <div className="flex justify-end gap-3 pt-4">
            {!isEditing ? (
              <Button
                type="button"
                onClick={() => setIsEditing(true)}
                className="px-8 h-12 bg-primary-blue hover:bg-primary-blue/90 text-white rounded-none text-sm"
              >
                Edit Practice
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="px-8 h-12 border-border-gray text-black hover:bg-gray-50 rounded-none text-sm"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || !hasChanges}
                  className="px-8 h-12 bg-primary-blue hover:bg-primary-blue/90 text-white rounded-none text-sm disabled:opacity-50"
                >
                  {isLoading ? "Saving..." : "Save Changes"}
                </Button>
              </>
            )}
          </div>
        )}
      </form>
    </div>
  );
};
